import {HttpErrorResponse} from "@angular/common/http";
import {Component, OnDestroy} from "@angular/core";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs/internal/Subscription";
import {finalize} from "rxjs/operators";
import {TatimeHttpClientService} from "src/app/data/tatime-http-client.service";
import {Tatime} from "src/app/models/SearchService/tatime.model";
import {LocalStorage} from "src/app/services/local-storage";
import {ModalPaymentService} from "src/app/services/ux/modal-payment.service";
import {toastrService} from "src/app/services/ux/toastr.service";
import {WalletService} from "src/app/services/wallet.service";
import {TransactionStatusEmitter} from "src/app/models/Payment/Emitters/transaction-status-emitter";
import {ModalAmountService} from "src/app/services/ux/modal-amount.service";
import { TranslateService } from "@ngx-translate/core";
@Component({
    templateUrl: "tatime-search.component.html"
})
export class TatimeSearchComponent implements OnDestroy {
    // #region Subscriptions
    subscription: Subscription;
    ccSubscription: Subscription;
    finishTransactionSubscription: Subscription;
    //#endregion

    searchForm: FormGroup;

    isLoading = false;

    hasResults = false;
    private _tatimeRecord: Tatime;
    public get tatimeRecord(): Tatime {
        return this._tatimeRecord;
    }

    public set tatimeRecord(value: Tatime) {
        this._tatimeRecord = value;
        this.hasResults = !!Object.keys(value).length;
    }

    hasParam = false;

    private calculatedAmount: number;
    komisionCC = 0;

    constructor(
        private fb: FormBuilder,
        private tatimeDataService: TatimeHttpClientService,
        private toastr: toastrService,
        private walletService: WalletService,
        private route: ActivatedRoute,
        private modalService: ModalPaymentService,
        private amountModalService: ModalAmountService,
        private ls: LocalStorage,
        private translateService: TranslateService
    ) {
        this.searchForm = this.fb.group({
            serialNumber: []
        });

        this.subscription = walletService.$walletReadyEmitter.subscribe(
            (isReady: boolean) => {
                if (isReady) {
                    this.processTatimeTransaction();
                }
            }
        );

        this.ccSubscription = walletService.$creditCardReadyEmitter.subscribe(
            (ccSelected: boolean) => {
                if (ccSelected) {
                    this.processTatimeTransaction(ccSelected);
                }
            }
        );

        this.finishTransactionSubscription = this.walletService.$transactionFinish.subscribe(
            (value) => {
                if (value) {
                    this.removeFinishedTransactionFromRecords();
                }
            }
        );

        this.route.params.subscribe((params: any) => {
            if (params.serialNumber) {
                this.searchForm.controls["serialNumber"].setValue(params.serialNumber);

                this.hasParam = true;
                this.onSearch();
            }
        });
    }

    onSearch() {
        if (this.searchForm.value.serialNumber == null) {
            return this.toastr.warning(this.translateService.instant("Services.FillInSerialNumber"));
        }
        this.isLoading = true;

        this.tatimeDataService
            .Search(this.searchForm.value.serialNumber)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response: Tatime) => {
                    this.tatimeRecord = response;
                },
                error: (error: HttpErrorResponse) => {
                    this.tatimeRecord = {} as Tatime;
                    switch (error.error) {
                        case "tatime_search_failed":
                            this.toastr.warning(this.translateService.instant("Services.TaxInformationNotFound"));
                            break;
                        case "no_invoices":
                            this.toastr.warning(this.translateService.instant("Services.TaxPaid"));
                            break;
                        default:
                            this.toastr.warning(this.translateService.instant("Services.ServerProblem"));
                            break;
                    }
                }
            });
    }

    async onPay() {
        const selectedInvoice = this.tatimeRecord;

        this.walletService.selectInvoice(selectedInvoice);
        this.walletService.amountOfTransaction = this.calculatedAmount;

        this.amountModalService.open();
    }

    async onAmountSubmitted(amount: number) {
        if (!amount) {
            this.toastr.error(this.translateService.instant("Services.ValueCannotBeZero"));
        }

        const commission = await this.GetCommission(amount);

        this.komisionCC = commission.amount;

        this.calculatedAmount = Number(amount) + Number(commission.amount);

        this.amountModalService.close();
        this.modalService.open();
    }

    // TODO: GetCommission from API (commision)
    private async GetCommission(amount: number) {
        return this.tatimeDataService.GetCommission(amount).toPromise();
    }

    processTatimeTransaction(creditCardMethod: boolean = false) {
        const tatimeInvoice = {
            invoice: this.walletService.getInvoice(),
            serviceFields: [
                {
                    FieldName: "serialNumber",
                    FieldValue: this.searchForm.value.serialNumber
                },
                {
                    FieldName: "amount",
                    FieldValue: this.calculatedAmount
                }
            ],
            ipAddress: "127.0.0.1",
            deviceId: "EternaWebPosta"
        };

        if (creditCardMethod) {
            this.tatimeDataService
                .PayCC(tatimeInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: (response) => {
                        if (!response.data) {
                            this.walletService.emitTransactionProgress({
                                    isSuccess: false,
                                    error: response.error.message
                                }
                            );
                            return;
                        }
                        this.walletService.emitTransactionProgress(
                            {
                                isSuccess: true,
                                error: null
                            }
                        );
                        this.ls.set("sn", "tatime");

                        window.location = (response.data.redirect);
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject: TransactionStatusEmitter = {
                            isSuccess: false,
                            error: error.message
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    },
                    complete: () => {
                        this.isLoading = false;
                    }
                });
        } else {
            this.tatimeDataService
                .PayWallet(tatimeInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: () => {
                        const emmiterObject = {
                            isSuccess: true,
                            error: null
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject = {
                            isSuccess: false,
                            error: error.error.message
                        };
                        this.walletService.emitTransactionProgress(emmiterObject);
                    }
                });
        }
    }

    private removeFinishedTransactionFromRecords() {
        this.tatimeRecord = {} as Tatime;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.ccSubscription.unsubscribe();
        this.finishTransactionSubscription.unsubscribe();
    }
}
